import { el } from "../dom.ts";

/**
 * Segmented control — a radio group that looks like a switch.
 *
 * A real `role="radiogroup"` of `role="radio"` buttons rather than a row of
 * toggles, because exactly one option is selected at a time and that is what
 * the radio pattern conveys. `aria-checked` is kept in sync on every option,
 * not only on the selected one: the ARIA tri-state distinction is the whole
 * reason `dom.ts` refuses to drop `aria-*` attributes whose value is `false`.
 *
 * This exists in the design system rather than in one route because there are
 * now three of these — tenant, presentation mode and receiver scenario — and
 * the third would otherwise have been the third hand-written copy of the same
 * selection and synchronisation logic.
 *
 * **Reference and configuration surfaces only.** It selects what is being
 * previewed; it never selects anything that affects money. A control that
 * changed an amount, a destination or an authorisation would need its own
 * component with its own confirmation semantics.
 */

export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

export interface SegmentedOptions<T extends string> {
  /** Accessible name for the group. Required — a bare radiogroup announces nothing. */
  readonly label: string;
  readonly options: ReadonlyArray<SegmentedOption<T>>;
  readonly value: T;
  readonly onChange: (value: T) => void;
}

export interface Segmented<T extends string> {
  readonly root: HTMLElement;
  /** Move the selection without invoking `onChange`. */
  select(value: T): void;
}

export function segmented<T extends string>(options: SegmentedOptions<T>): Segmented<T> {
  const buttons = options.options.map((option) =>
    el("button", {
      class: "nura-segmented__option",
      text: option.label,
      attrs: {
        type: "button",
        role: "radio",
        "aria-checked": option.value === options.value,
      },
      data: { value: option.value },
    }),
  );

  function select(value: T): void {
    for (const [index, node] of buttons.entries()) {
      node.setAttribute("aria-checked", String(options.options[index]?.value === value));
    }
  }

  const root = el(
    "div",
    { class: "nura-segmented", attrs: { role: "radiogroup", "aria-label": options.label } },
    buttons,
  );

  root.addEventListener("click", (event) => {
    const target = (event.target as Element | null)?.closest(".nura-segmented__option");
    if (!(target instanceof HTMLButtonElement)) return;
    const index = buttons.indexOf(target);
    const option = options.options[index];
    if (option === undefined) return;
    select(option.value);
    options.onChange(option.value);
  });

  return { root, select };
}
